import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { employeeApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import ApprovalTimeline from '../../components/employee/ApprovalTimeline.jsx';
import { ArrowLeft } from 'lucide-react';
import { formatDays } from '@/lib/utils';

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };

// A single-day request reads as one date -- "Sep 17", never "Sep 17 to Sep 17".
function formatSpan(start, end) {
  return start === end ? formatDate(start) : `${formatDate(start)} to ${formatDate(end)}`;
}

function formatDate(d) {
  if (!d) return '\u2014';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

export default function RequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  async function load() {
    try {
      const res = await employeeApi.getRequest(id);
      setRequest(res.request);
    } catch (err) {
      toast.error(`Failed to load request: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCancel() {
    setCancelling(true);
    try {
      await employeeApi.cancelRequest(id);
      toast.success('Request cancelled');
      setShowCancel(false);
      await load();
    } catch (err) {
      toast.error(`Cancel failed: ${err.message}`);
    } finally {
      setCancelling(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="ml-3 text-sm">Loading...</span>
      </div>
    );
  }

  if (!request) {
    return <div className="p-8 text-center text-sm text-muted-foreground">Request not found</div>;
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Link to="/my-requests" className="mb-4 inline-flex items-center gap-1 text-sm text-primary no-underline hover:underline">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to My Requests
      </Link>

      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="mb-2 flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{request.typeName || request.typeId}</h1>
            <Badge variant={STATUS_VARIANT[request.status] || 'default'}>{request.status}</Badge>
          </div>
          <p className="text-muted-foreground">
            {formatDays(request.days)} day{request.days !== 1 ? 's' : ''} &middot; {formatSpan(request.startDate, request.endDate)}
          </p>
        </div>
        <div className="flex gap-2">
          {(request.status === 'pending' || request.status === 'rejected') && (
            <Button variant="secondary" onClick={() => setShowEdit(true)}>
              {request.status === 'rejected' ? 'Edit & resubmit' : 'Edit'}
            </Button>
          )}
          {(request.status === 'pending' || request.status === 'approved') && (
            <Button variant="danger" onClick={() => setShowCancel(true)}>Cancel request</Button>
          )}
        </div>
      </div>

      {/* Details */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            <DetailItem label="Leave Type" value={request.typeName || request.typeId} />
            <DetailItem label="Days" value={`${formatDays(request.days)} working day${request.days !== 1 ? 's' : ''}`} />
            <DetailItem label="Start Date" value={formatDate(request.startDate)} />
            <DetailItem label="End Date" value={formatDate(request.endDate)} />
            <DetailItem label="Submitted" value={request.submittedAt ? new Date(request.submittedAt).toLocaleString() : '\u2014'} />
          </div>
          {request.reason && (
            <div className="mt-4 border-t border-border pt-4">
              <div className="mb-1 text-xs text-muted-foreground">Reason</div>
              <div className="text-sm">{request.reason}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Timeline */}
      <div className="mb-6">
        <h2 className="mb-4 text-base font-semibold">Approval Progress</h2>
        <ApprovalTimeline
          chainSnapshot={request.chainSnapshot}
          currentStep={request.currentStep}
          status={request.status}
        />
      </div>

      {/* Cancel modal */}
      {showCancel && (
        <Modal
          isOpen={true}
          onClose={() => setShowCancel(false)}
          title="Cancel Request"
          width="400px"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowCancel(false)}>Keep request</Button>
              <Button variant="danger" onClick={handleCancel} loading={cancelling}>Yes, cancel</Button>
            </>
          }
        >
          <div className="space-y-2">
            <p className="text-sm">Are you sure you want to cancel this leave request?</p>
            <p className="text-sm text-muted-foreground">
              {request.typeName} &middot; {formatSpan(request.startDate, request.endDate)} &middot; {formatDays(request.days)} days
            </p>
            {request.status === 'approved' && (
              <p className="text-xs text-warning">This will restore your leave balance and remove the event from calendars.</p>
            )}
          </div>
        </Modal>
      )}

      {/* Edit confirmation — editing (re)starts the approval workflow. */}
      {showEdit && (
        <Modal
          isOpen={true}
          onClose={() => setShowEdit(false)}
          title={request.status === 'rejected' ? 'Edit & resubmit this request?' : 'Edit this request?'}
          width="440px"
          footer={
            <>
              <Button variant="ghost" onClick={() => setShowEdit(false)}>Keep as is</Button>
              <Button onClick={() => navigate(`/request?edit=${encodeURIComponent(id)}`)}>
                {request.status === 'rejected' ? 'Edit & resubmit' : 'Edit request'}
              </Button>
            </>
          }
        >
          {request.status === 'rejected' ? (
            <p className="text-sm">
              Make your changes and resubmit — the request goes back through the approval chain from the start for a fresh review.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm">
                Editing this request <strong>resets its approval</strong> — it goes back to the start of the approval chain and your approver(s) will review it again.
              </p>
              <p className="text-sm text-muted-foreground">
                Any approvals already given on this request will be cleared.
              </p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-medium">{value}</div>
    </div>
  );
}
